import React, { useImperativeHandle, useRef, useState } from 'react';

type MultiSelectInputBoxProps = {
  availableOptions: string[];
  chips: string[];
  label?: string;
  setChips: (inpArr: string[]) => void;
  hideBorder?: boolean;
  backgroundColor?: string;
  height?: number;
  acceptsSentence?: boolean;
};
/**
 * A component that represents a multi-select input box with chips.
 * @param props - MultiSelectInputBoxProps.
 * @returns A React element representing the multi-select input box.
 */
const MultiSelectInputBox = React.forwardRef((props: MultiSelectInputBoxProps, ref) => {
  const {
    availableOptions,
    chips,
    setChips,
    label,
    hideBorder,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    backgroundColor,
    height,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    acceptsSentence
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [currentInput, setCurrentInput] = useState('');
  const [focused, setFocused] = useState(false);
  const filteredOptions = availableOptions.filter((option) => {
    if (option.toLowerCase().includes(currentInput.toLowerCase()) && !chips.includes(option)) {
      return true;
    }
    return false;
  });

  /**
   * Handles the addition of a chip based on the current input value.
   * @returns - New chips.
   */
  function handleAddChip(): string[] {
    const trimmedInput = currentInput.trim();
    if (trimmedInput === '') return chips;

    if (availableOptions.includes(trimmedInput) || chips.includes(trimmedInput)) {
      // If the input matches an existing option, don't add it again.
      setCurrentInput('');
      return chips;
    }
    const newChips = [...chips, trimmedInput];
    setChips(newChips);
    setCurrentInput('');
    return newChips;
  }

  /**
   * Handles the selection of an option from the filtered options list.
   * @param option - The selected option.
   */
  const handleOptionSelect = (option: string): void => {
    setChips([...chips, option]);
    setCurrentInput('');
  };

  /**
   * Handles the deletion of a chip based on its index.
   * @param index - The index of the chip to be deleted.
   */
  const handleDeleteChip = (index: number): void => {
    const updatedChips = chips.filter((_, i) => i !== index);
    setChips(updatedChips);
  };

  /**
   * Handles Key down function.
   * @param e - Keyboard event.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && currentInput.length === 0) {
      e.preventDefault();
      handleDeleteChip(chips.length - 1);
    }
    if (e.key === 'Enter' || (!acceptsSentence && e.key === ' ')) {
      e.preventDefault();
      handleAddChip();
    }
  };

  useImperativeHandle(ref, () => ({
    /**
     * Function to add chip on submit.
     * @returns - New chips.
     */
    forceAddChip: () => {
      const newChips = handleAddChip();
      return newChips;
    }
  }));

  return (
    <div className="mt-4 flex flex-1 flex-col relative">
      {label && <div className="text-gray-500 dark:text-gray-500 text-sm">{label}</div>}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div
        tabIndex={0}
        role="button"
        onClick={() => inputRef.current?.focus()}
        style={{ backgroundColor, minHeight: height }}
        className={`${
          !hideBorder && 'border'
        } p-2 mt-1 flex flex-1 flex-wrap border-gray-300 dark:border-gray-700 rounded focus:dark:border-primary-300 focus-within:dark:ring-transparent cursor-text`}
      >
        {chips.map((chip, index) => (
          <div
            key={chip}
            className="flex mx-1 pl-2 pr-1 items-center py-0.5 dark:bg-gray-800 rounded text-primary-100 text-sm gap-1 h-fit"
          >
            {chip}
            <span
              role="button"
              tabIndex={0}
              onClick={() => handleDeleteChip(index)}
              onKeyDown={() => handleDeleteChip(index)}
              className="flex items-center cursor-pointer material-icons-outlined text-xs text-gray-500 hover:text-gray-700 "
            >
              close
            </span>
          </div>
        ))}
        <input
          type="text"
          style={{ backgroundColor }}
          className={`w-full block h-12 outline-none items-start pt-1.5 py-1.5 px-3.5 text-gray-300 dark:text-gray-300 placeholder-gray-400 text-start ${'bg-background-900'}`}
          value={currentInput}
          ref={inputRef}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => setCurrentInput(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
      {focused && filteredOptions.length > 0 && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          onMouseDown={(e) => e.preventDefault()}
          className="border mt-1 rounded border-gray-300 dark:border-gray-700 absolute z-10 bottom-0 dark:bg-background-900"
        >
          {filteredOptions.map((option) => (
            <div
              key={option}
              role="button"
              tabIndex={0}
              onClick={() => handleOptionSelect(option)}
              onKeyDown={() => handleOptionSelect(option)}
              className="cursor-pointer px-5 py-2 hover:bg-gray-100 dark:hover:bg-container-300 rounded text-primary-100 text-xs"
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default MultiSelectInputBox;
